import { NavLink } from 'react-router-dom';
import {
  ClipboardList,
  BookOpen,
  Settings,
  X,
  Home,
  CalendarDays,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  Clock,
  CalendarClock,
  Rocket,
  ChevronDown,
  ChevronRight,
  Send,
} from 'lucide-react';
import { useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { useDomains } from '../../hooks/useDomains';
import { useAppSidebarCollapsed } from '../../hooks/useAppSidebarCollapsed';
import { getDomainColors } from '../../lib/domainColors';
import { useBranding } from '../../contexts/BrandingContext';
import { useDomainStatuses, type DomainStatus } from '../../hooks/useDomainStatuses';
import { CONTAINER_REGISTRY } from '../../lib/containers';
import { useMe } from '../../hooks/useMe';

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Domain accent colors — outline for icon/border, fill for button background. */
  accentOutline?: string;
  accentFill?: string;
  /** Domain status indicator color. */
  status?: DomainStatus;
};

// Primary nav (above divider). Wiki and Organizers render immediately after
// Lessons Learned (moved out of the secondary nav).
const primaryNavItems: NavItem[] = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/tasks', label: 'Tasks', icon: ClipboardList },
  { to: '/wiki', label: 'Wiki', icon: BookOpen },
  { to: '/retrospective', label: 'Retrospective', icon: Clock },
  { to: '/organizers', label: 'Organizers', icon: Users },
];

// BSides Live admin section — separated from the rest of the nav. Two buttons:
// the BSides Live admin (replacing the Day-Of App domain entry) and the
// Command Center for organizers. Both are visible to all signed-in
// organizers; per-tab access inside each page is gated by isAdmin.
const liveNavItems: NavItem[] = [
  { to: '/day-of-app', label: 'BSides Live', icon: CalendarClock },
  { to: '/dayof', label: 'Command Center', icon: Rocket },
];

// Secondary nav (settings). Wiki and Organizers were moved up under the
// primary nav (right after Lessons Learned); Settings stays here alone.
const secondaryNavItems: NavItem[] = [
  { to: '/settings', label: 'Settings', icon: Settings },
];

interface SidebarProps {
  onClose?: () => void;
  isCollapsed?: boolean;
}

export default function Sidebar({ onClose, isCollapsed = false }: SidebarProps) {
  const { logoUrl } = useBranding();
  const { domains, list: domainList } = useDomains();
  const { toggleAppSidebar } = useAppSidebarCollapsed();
  const domainStatuses = useDomainStatuses(domainList);
  const [dayOfOpen, setDayOfOpen] = useState(true);
  const { isAdmin } = useMe();
  const CollapseIcon = isCollapsed ? PanelLeftOpen : PanelLeftClose;
  const collapseLabel = isCollapsed ? 'Expand navigation' : 'Collapse navigation';
  const domainNavItems: NavItem[] = CONTAINER_REGISTRY.map((c) => {
    const stored = domains[c.id];
    const colors = getDomainColors(domains, c.id);
    return {
      to: c.route,
      label: stored?.name ?? c.name,
      icon: c.icon,
      accentOutline: colors?.outline,
      accentFill: colors?.fill,
      status: domainStatuses[c.id],
    };
  });

  const statusMeta = (status: DomainStatus) => {
    const colorClass =
      status === 'red'
        ? 'bg-danger-500'
        : status === 'yellow'
          ? 'bg-warning-400'
          : 'bg-success-500';
    const srText =
      status === 'red'
        ? 'urgent items'
        : status === 'yellow'
          ? 'upcoming items'
          : 'all clear';
    return { colorClass, srText };
  };

  const statusDot = (status: DomainStatus | undefined, srLabel: string) => {
    if (!status) return null;
    const { colorClass, srText } = statusMeta(status);
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${colorClass}`}
        role="img"
        aria-label={`${srLabel}: ${srText}`}
      />
    );
  };

  const statusAccent = (status: DomainStatus | undefined, srLabel: string) => {
    if (!status) return null;
    const { colorClass, srText } = statusMeta(status);
    return (
      <span
        className={`absolute right-0 top-1 bottom-1 w-1 rounded-full ${colorClass}`}
        role="img"
        aria-label={`${srLabel}: ${srText}`}
      />
    );
  };

  const renderItem = (item: NavItem) => {
    const hasDomainColors = !!(item.accentOutline && item.accentFill);

    return (
      <li key={item.to} role="listitem">
        <NavLink
          to={item.to}
          onClick={onClose}
          title={isCollapsed ? item.label : undefined}
          aria-label={isCollapsed ? item.label : undefined}
          className={({ isActive }) =>
            hasDomainColors
              ? `flex items-center overflow-hidden rounded-lg transition-colors min-h-[44px] text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 sidebar-accent${isActive ? ' current' : ''}`
              : `flex items-center ${
                  isCollapsed ? 'justify-center px-0' : 'gap-3 px-3'
                } rounded-lg transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
          }
          style={
            hasDomainColors
              ? {
                  '--sidebar-accent': item.accentOutline,
                  '--sidebar-accent-fill': item.accentFill,
                } as React.CSSProperties
              : undefined
          }
        >
          {({ isActive }) =>
            hasDomainColors ? (
              <>
                <span
                  className={`relative flex items-center justify-center w-10 self-stretch flex-shrink-0 ${isCollapsed ? 'w-full' : ''}`}
                  aria-hidden={!isCollapsed || !item.status}
                >
                  <item.icon className="w-5 h-5" style={{ color: item.accentFill }} />
                  {isCollapsed && item.status && statusAccent(item.status, item.label)}
                </span>
                {!isCollapsed && (
                  <span
                    className="text-sm font-medium truncate px-3 self-stretch flex items-center flex-1 rounded-l-lg -ml-1 gap-2"
                    style={{ backgroundColor: item.accentFill }}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.status && (
                      <span className="ml-auto flex-shrink-0">
                        {statusDot(item.status, item.label)}
                      </span>
                    )}
                  </span>
                )}
                {isActive && <span className="sr-only">(current page)</span>}
              </>
            ) : (
              <>
                <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {!isCollapsed && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
                {isActive && <span className="sr-only">(current page)</span>}
              </>
            )
          }
        </NavLink>
      </li>
    );
  };

  return (
    <aside
      className={`${
        isCollapsed ? 'w-14' : 'w-64'
      } h-full bg-gray-900 text-white flex flex-col transition-[width] duration-200 ease-in-out`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Branding + collapse/close row */}
      <div
        className={`${
          isCollapsed ? 'p-2 flex-col' : 'p-4 justify-between'
        } border-b border-gray-800 flex items-center gap-2`}
      >
        {!isCollapsed && (
          <div className="min-w-0 flex items-center gap-2">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                className="w-8 h-8 object-contain flex-shrink-0 rounded"
              />
            )}
            <div className="min-w-0">
              <div className="text-lg font-bold text-primary-400 truncate">BSides Boulder</div>
              <div className="text-xs text-gray-400 truncate">Organizer Toolkit</div>
            </div>
          </div>
        )}
        {isCollapsed && logoUrl && (
          <img
            src={logoUrl}
            alt="Logo"
            className="w-8 h-8 object-contain rounded"
          />
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Desktop collapse toggle — now lives on the bar that collapses */}
          <button
            type="button"
            onClick={toggleAppSidebar}
            className="hidden lg:inline-flex p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            aria-pressed={isCollapsed}
            aria-controls="sidebar-nav"
            aria-label={collapseLabel}
            title={collapseLabel}
          >
            <CollapseIcon className="w-5 h-5" aria-hidden="true" />
          </button>

          {/* Close button — mobile only, when sidebar is open */}
          {onClose && !isCollapsed && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              aria-label="Close navigation menu"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <nav
        id="sidebar-nav"
        className={`flex-1 min-h-0 ${isCollapsed ? 'p-2' : 'p-4'} overflow-y-auto overflow-x-hidden`}
      >
        <ul className="space-y-1" role="list">
          {renderItem(primaryNavItems[0])}
          <MeetingNotesNavRow isCollapsed={isCollapsed} onClose={onClose} />
          {primaryNavItems.slice(1).map(renderItem)}
          {/* Email Blast sits directly under Organizers (admin-only). */}
          {isAdmin &&
            renderItem({ to: '/email-blast', label: 'Email Blast', icon: Send })}
        </ul>

        <hr className="my-4 border-gray-700" />
        <CollapsibleNavSection
          title="Day Of"
          items={liveNavItems}
          isCollapsed={isCollapsed}
          open={dayOfOpen}
          setOpen={setDayOfOpen}
          renderItem={renderItem}
        />

        <hr className="my-4 border-gray-700" />

        <ul className="space-y-1" role="list">
          {domainNavItems.map(renderItem)}
        </ul>

        <hr className="my-4 border-gray-700" />

        <ul className="space-y-1" role="list">
          {secondaryNavItems.map(renderItem)}
        </ul>
      </nav>
    </aside>
  );
}

/**
 * A nav group with an uppercase section header that collapses/expands. In the
 * narrow (w-14) rail there's no room for the text toggle, so the items render
 * always-expanded under an aria-labelled list instead.
 */
function CollapsibleNavSection({
  title,
  items,
  isCollapsed,
  open,
  setOpen,
  renderItem,
}: {
  title: string;
  items: NavItem[];
  isCollapsed: boolean;
  open: boolean;
  setOpen: (fn: (open: boolean) => boolean) => void;
  renderItem: (item: NavItem) => React.ReactNode;
}) {
  const navId = `${title.toLowerCase().replace(/\s+/g, '-')}-nav`;

  if (isCollapsed) {
    return (
      <ul className="space-y-1" role="list" aria-label={`${title} navigation`}>
        {items.map(renderItem)}
      </ul>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={navId}
        className="w-full flex items-center gap-2 px-3 min-h-[44px] rounded-lg text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        )}
        <span>{title}</span>
      </button>
      {open && (
        <ul id={navId} className="space-y-1 mt-1" role="list" aria-label={`${title} navigation`}>
          {items.map(renderItem)}
        </ul>
      )}
    </>
  );
}

function MeetingNotesNavRow({
  isCollapsed,
  onClose,
}: {
  isCollapsed: boolean;
  onClose?: () => void;
}) {
  const label = 'Meetings';
  return (
    <li role="listitem">
      <NavLink
        to="/meetings"
        onClick={onClose}
        title={isCollapsed ? label : undefined}
        aria-label={isCollapsed ? label : undefined}
        className={({ isActive }) =>
          `flex items-center ${
            isCollapsed ? 'justify-center px-0 w-full' : 'gap-3 px-3'
          } rounded-lg transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
            isActive
              ? 'bg-primary-600 text-white'
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }`
        }
      >
        <CalendarDays className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        {!isCollapsed && (
          <span className="text-sm font-medium truncate">{label}</span>
        )}
      </NavLink>
    </li>
  );
}

